import React, { useState, useEffect, useReducer, createContext } from "react";
import { Form } from "antd";
import genderOptions from "../../../jsonLib/gender_options.json";
import allProvince from "../../../jsonLib/allProvince.json";
import allMunicipalities from "../../../jsonLib/allMunicipalities.json";
import {
  getBarangayOption,
  verifyCitizen,
  sendOTP,
  validateOTP,
  uploadCitizenPhoto,
  registerCitizen,
  generateIDCard,
  getModelChoices,
  getReligionChoices,
  getOccupationChoices,
} from "../../../services/public_registration.service";
import Swal from "sweetalert2";
import moment from "moment";
import { containsSpecialFontCharacters } from "utils";

export const NewCitizenContext = createContext({});

const defaultState = {
  citizenDetails: {
    first_name: "",
    last_name: "",
    middle_name: "",
    name_extension: "",
    birthdate_year: "",
    birthdate_month: "",
    birthdate_day: "",
    gender: null,
    phone_number: "",
    is_voter: null,
    barangay: null,
    address: "",
    municipality: null,
    email: "",
    province: null,
    profile: "",
    principal: "",
    is_verified: false,
    fb_profile_link: null,
    code: null,
    monthly_income: null,
    additional_details: {
      religion: null,
      occupation: null,
      household_members_count: null,
    },
  },
  socialServiceDetails: {
    customer: null,
    service: null,
    status: "pending",
    value_requested: "0",
    has_representative: null,
    other_details: {},
  },
  similarCitizens: [],
  showSimilarResults: false,
  isHidden: false,
  shouldGoToSummary: false,
  resetWizard: false,
};

const defaultStateFn = key => {
  return key ? defaultState[key] : defaultState;
};

const stateDispatchReducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_CITIZEN_DETAILS":
      return {
        ...state,
        ...action.payload,
      };
    case "RESET":
      return defaultStateFn();

    default: {
      if (action.key) {
        if (typeof action.payload === "object") {
          return {
            ...state,
            [action.key]: {
              ...state[action.key],
              ...action.payload,
            },
          };
        }

        return {
          ...state,
          [action.key]: action.payload,
        };
      }

      return state;
    }
  }
};

const NewCitizenProvider = ({ children }) => {
  const [state, stateDispatch] = useReducer(stateDispatchReducer, defaultStateFn());

  const [provinceOptions, setProvinceOptions] = useState();
  const [selectedProvince, setSelectedProvince] = useState();
  const [municipalityOptions, setMunicipalityOptions] = useState();
  const [selectedMunicipality, setSelectedMunicipality] = useState();
  const [barangayOption, setBarangayOptions] = useState();
  const [agencyOptions, setAgencyOptions] = useState();
  const [selectedAgency, setSelectedAgency] = useState();
  const [selectedSocialService, setSelectedSocialService] = useState();
  const [socialServiceOptions, setSocialServiceOptions] = useState();
  const [filteredSocialServiceOptions, setFilteredSocialServiceOptions] = useState();
  const [hasRepresentative, setHasRepresentative] = useState();
  const [isNumberValid, setIsNumberValid] = useState(false);
  const [messageID, setMessageID] = useState();
  const [otp, setOTP] = useState();
  const [ATCOptions, setATCOptions] = useState();
  const [IDCard, setIDCard] = useState({ url: "", filename: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [agreement, setAgreement] = useState(false);
  const [modelOptions, setModelOptions] = useState();
  const [religionOptions, setReligionOptions] = useState();
  const [occupationOptions, setOccupationOptions] = useState();

  const [form] = Form.useForm();

  function formatPhoneNumber(value) {
    // format the input value as a Philippine phone number
    if (!value) return "";
    value = value.toString();
    if (value.startsWith("0")) return value.replace(/^0/, "63");
    if (value.startsWith("63")) return value;

    // setVal(`63${value}`);
    return `63${value}`;
  }

  function parsePhoneNumber(value) {
    // parse the input value as a number
    if (!value) return 0;
    return value.toString().replace(/^(0|63)/, "");
  }

  const representativeOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  useEffect(() => {
    const filteredProvince = allProvince.map(province => ({
      ...province,
      label: province?.name,
      value: province?.id,
    }));

    setProvinceOptions(filteredProvince);
  }, []);

  useEffect(() => {
    if (selectedProvince) {
      const filteredMunicipality = allMunicipalities
        .filter(municipality => municipality?.province?.id === selectedProvince)
        .map(municipality => ({
          ...municipality,
          label: municipality?.name,
          value: municipality?.id,
        }));
      form.setFieldsValue({ municipality: undefined });
      setSelectedMunicipality(null);
      setMunicipalityOptions(filteredMunicipality);
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (selectedMunicipality) {
      setIsLoading(true);
      setBarangayOptions([]);
      const fetchBarangay = async () => {
        const barangayOption = await getBarangayOption(selectedMunicipality);
        const filteredBarangayOption = barangayOption.results.map(barangay => ({
          ...barangay,
          label: barangay?.name,
          value: barangay?.id,
        }));
        setIsLoading(false);
        setBarangayOptions(filteredBarangayOption);
      };

      fetchBarangay().catch(console.error);
    }
  }, [selectedMunicipality]);

  useEffect(() => {
    const fetchModelChoices = async () => {
      try {
        const modelOptions = await getModelChoices();
        // Check different possible response structures
        const monthlyIncomeData = modelOptions?.monthly_income || modelOptions?.results || modelOptions || [];
        const filteredModelOptions = monthlyIncomeData.map(model => ({
          ...model,
          label: model?.name,
          value: model?.id,
        }));
        setModelOptions(filteredModelOptions);
      } catch (error) {
        console.error("Error fetching model choices:", error);
        setModelOptions([]);
      }
    };
    fetchModelChoices().catch(console.error);

    const fetchReligionChoices = async () => {
      try {
        const religionOptions = await getReligionChoices();
        const religionData = religionOptions?.results || religionOptions || [];
        const filteredReligionOptions = religionData.map(religion => ({
          ...religion,
          label: religion?.name,
          value: religion?.id,
        }));
        setReligionOptions(filteredReligionOptions);
      } catch (error) {
        console.error("Error fetching religion choices:", error);
        setReligionOptions([]);
      }
    };
    fetchReligionChoices().catch(console.error);

    const fetchOccupationChoices = async () => {
      try {
        const occupationOptions = await getOccupationChoices();
        const occupationData = occupationOptions?.results || occupationOptions || [];
        const filteredOccupationOptions = occupationData.map(occupation => ({
          ...occupation,
          label: occupation?.name,
          value: occupation?.id,
        }));
        setOccupationOptions(filteredOccupationOptions);
      } catch (error) {
        console.error("Error fetching occupation choices:", error);
        setOccupationOptions([]);
      }
    };
    fetchOccupationChoices().catch(console.error);
  }, []);

  // useEffect(() => {
  //   const fetchAgency = async () => {
  //     const agencyOptions = await getAgency();
  //     const filteredAgencyOptions = agencyOptions.map(agency => ({
  //       ...agency,
  //       label: agency?.name,
  //       value: agency?.id,
  //     }));
  //     setAgencyOptions(filteredAgencyOptions);
  //     setSelectedAgency(filteredAgencyOptions[0]?.value);
  //   };

  //   fetchAgency().catch(console.error);
  // }, []);

  // get all social service
  // useEffect(() => {
  //   const fetchSocialService = async () => {
  //     setIsLoading(true);
  //     const socialServiceOptions = await getSocialService();
  //     const filteredSocialService = socialServiceOptions.map(socialService => ({
  //       ...socialService,
  //       label: socialService?.service_name,
  //       value: socialService?.id,
  //     }));
  //     setIsLoading(false);
  //     setSocialServiceOptions(filteredSocialService);
  //   };

  //   fetchSocialService().catch(console.error);
  // }, []);

  // changed drop down option for social service
  useEffect(() => {
    if (selectedAgency) {
      setSelectedSocialService(null);
      const filteredOption = socialServiceOptions?.filter(
        socialService => socialService?.agency?.id === selectedAgency,
      );
      setFilteredSocialServiceOptions(filteredOption);
    }
  }, [selectedAgency]);

  useEffect(() => {
    if (isNumberValid) {
      Swal.fire({
        title: "Oops!",
        text: "Ang numero na ito ay ginamit na ng tatlong beses. Mangyaring maglagay ng wastong Mobile Number.",
        allowOutsideClick: true,
        showCancelButton: true,
        cancelButtonText: "Exit",
        showConfirmButton: false,
        width: "80%",
      });
    }
  }, [isNumberValid]);

  const checkUserExist = async () => {
    // check if name contains other text characters
    const containsSpecialFonts = ["first_name", "last_name", "name_extension"].some(fieldName =>
      containsSpecialFontCharacters(state.citizenDetails[fieldName]),
    );
    if (containsSpecialFonts) {
      Swal.fire({
        title: "Oops!",
        text: "Invalid name!",
        allowOutsideClick: true,
        showCancelButton: true,
        cancelButtonText: "Exit",
        showConfirmButton: false,
        width: "80%",
      });
      return false;
    }

    // Check for existing citizens using verifyCitizen service
    const existingCitizens = await verifyCitizen(
      state.citizenDetails.first_name,
      state.citizenDetails.last_name,
      state.citizenDetails.name_extension,
      moment(
        `${state.citizenDetails.birthdate_year}-${state.citizenDetails.birthdate_month}-${state.citizenDetails.birthdate_day}`,
        "YYYY-MM-DD",
        false,
      ).format("YYYY-MM-DD"),
    );

    if (existingCitizens && Object.keys(existingCitizens).length > 0) {
      // Convert object to array if it's not already an array
      const citizensArray = Array.isArray(existingCitizens) ? existingCitizens : Object.values(existingCitizens);

      // Store existing citizens data and show similar results
      stateDispatch({
        key: "similarCitizens",
        payload: citizensArray,
      });
      stateDispatch({
        key: "showSimilarResults",
        payload: true,
      });
      stateDispatch({
        key: "isHidden",
        payload: true,
      });

      return false;
    }

    return true; // Continue with registration if no exact match or similar citizens found
  };

  const sendOTPMobile = async () => {
    const otp = await sendOTP(state.citizenDetails.phone_number);
    setMessageID(otp?.message_id);
  };

  const verifyOTP = async () => {
    const isValidOTP = await validateOTP(messageID, otp);
    if (!isValidOTP || otp.length < 6 || otp === undefined) {
      Swal.fire({
        title: "Oops!",
        text: "Mali ang iyong nilagay na OTP",
        allowOutsideClick: true,
        showCancelButton: true,
        cancelButtonText: "Exit",
        showConfirmButton: false,
        width: "80%",
      });
    }
    return isValidOTP;
  };

  const verifyPhoto = async () => {
    const hasPhoto = Boolean(state.citizenDetails.profile);
    if (!hasPhoto) {
      Swal.fire({
        title: "Oops!",
        text: "Maglagay ng picture.",
        allowOutsideClick: true,
        showCancelButton: true,
        cancelButtonText: "Exit",
        showConfirmButton: false,
        width: "80%",
      });
    }
    return hasPhoto;
  };

  const uploadPhoto = async () => {
    const isUpload = await uploadCitizenPhoto(state.citizenDetails.profile);
    return isUpload;
  };

  const transformCitizenDetailsToParams = () => {
    const citizenDetailsToParam = { ...state.citizenDetails };
    delete citizenDetailsToParam.birthdate_year;
    delete citizenDetailsToParam.birthdate_month;
    delete citizenDetailsToParam.birthdate_day;
    delete citizenDetailsToParam.card_number;

    citizenDetailsToParam.birth_date = moment(
      `${state.citizenDetails.birthdate_year}-${state.citizenDetails.birthdate_month}-${state.citizenDetails.birthdate_day}`,
      "YYYY-MM-DD",
      false,
    ).format("YYYY-MM-DD");

    // convert empty string parameters to `null`
    ["fb_profile_link", "middle_name"].forEach(fieldName => {
      if (!citizenDetailsToParam?.[fieldName]) {
        citizenDetailsToParam[fieldName] = null;
      }
    });

    return citizenDetailsToParam;
  };

  const generateAndSetIDCard = async citizenId => {
    try {
      const res = await generateIDCard(citizenId);
      const filename = `${citizenId}.png`;
      setIDCard({ url: URL.createObjectURL(res), filename });
      return true;
    } catch (error) {
      console.error("Error generating ID card:", error);
      return false;
    }
  };

  const registerCitizenDetails = async () => {
    if (state.citizenDetails.profile) {
      const params = transformCitizenDetailsToParams();
      const register = await registerCitizen(params);
      const response = await register.json();
      if (register.status === 200 || register.status === 201) {
        const success = await generateAndSetIDCard(response.id);
        return success;
      } else {
        Swal.fire({
          title: "Oops!",
          text: `${response?.[0] || response?.non_field_errors?.[0] || response?.code?.[0] || "Something went wrong"}`,
          allowOutsideClick: true,
          showCancelButton: true,
          cancelButtonText: "Exit",
          showConfirmButton: false,
          width: "80%",
        });
        return false;
      }
      // const newSocialServiceDetails = {
      //   ...state.socialServiceDetails,
      //   customer: register.id,
      // };
      // await registerCitizenSocialService(newSocialServiceDetails);
    } else {
      Swal.fire({
        title: "Oops!",
        text: "Kinakailangan mag-upload ng iyong litrato",
        allowOutsideClick: true,
        showCancelButton: true,
        cancelButtonText: "Exit",
        showConfirmButton: false,
        width: "80%",
      });
      return false;
    }
  };

  const conditionalRegisterValidator = async () => {
    if (state.showSimilarResults) {
      if (state.citizenDetails.id) {
        return await generateAndSetIDCard(state.citizenDetails.id);
      }
      return true;
    }

    return await registerCitizenDetails();
  };

  // useEffect(() => {
  //   if (selectedMunicipality) {
  //     const fetchATC = async () => {
  //       const atcOptions = await getATC(selectedMunicipality);
  //       const filteredATCOptions = atcOptions.map(atc => ({
  //         ...atc,
  //         label: atc?.atc_name,
  //         value: atc?.id,
  //       }));
  //       setATCOptions(filteredATCOptions);
  //     };

  //     fetchATC().catch(console.error);
  //   }
  // }, [selectedMunicipality]);

  const downloadCitizenID = () => {
    const link = document.createElement("a");
    link.href = IDCard.url;
    link.download = IDCard.filename;
    link.click();
  };

  const handleSimilarCitizenSelect = citizen => {
    // Update citizen details with the selected citizen's data
    stateDispatch({
      key: "citizenDetails",
      payload: {
        ...state.citizenDetails,
        id: citizen.id, // Preserve the citizen ID
        first_name: citizen.first_name,
        last_name: citizen.last_name,
        middle_name: citizen.middle_name || "",
        name_extension: citizen.name_extension || "",
        birthdate_year: citizen.birth_date ? moment(citizen.birth_date).format("YYYY") : "",
        birthdate_month: citizen.birth_date ? moment(citizen.birth_date).format("MM") : "",
        birthdate_day: citizen.birth_date ? moment(citizen.birth_date).format("DD") : "",
        birth_date: citizen.birth_date,
        profile: citizen.profile || "",
        fb_profile_link: citizen.fb_profile_link || "",
        card_number: citizen.card_number,
        barangay: citizen.barangay?.id || "",
        municipality: citizen.municipality?.id || "",
        province: citizen.municipality?.province?.id || "",
        address: citizen.address || "",
        additional_details: citizen.additional_details || {},
      },
    });

    // Set flag to navigate to Summary step
    stateDispatch({
      key: "shouldGoToSummary",
      payload: true,
    });
  };

  const handleNewRegistration = () => {
    // Reset similar results and continue with registration
    stateDispatch({
      key: "showSimilarResults",
      payload: false,
    });
    stateDispatch({
      key: "similarCitizens",
      payload: [],
    });
    stateDispatch({
      key: "isHidden",
      payload: false,
    });

    // Reset all state to default using the RESET action
    stateDispatch({
      type: "RESET",
    });

    // Reset form if it exists
    if (form) {
      form.resetFields();
    }

    // Force wizard to reset by setting a flag that will be handled in the component
    stateDispatch({
      key: "resetWizard",
      payload: true,
    });
  };

  const payload = {
    genderOptions,
    state,
    stateDispatchMain: stateDispatch,
    provinceOptions,
    municipalityOptions,
    setSelectedProvince,
    setSelectedMunicipality,
    barangayOption,
    setBarangayOptions,
    selectedProvince,
    selectedMunicipality,
    agencyOptions,
    representativeOptions,
    formatPhoneNumber,
    parsePhoneNumber,
    setSelectedAgency,
    selectedSocialService,
    filteredSocialServiceOptions,
    hasRepresentative,
    setHasRepresentative,
    setSelectedSocialService,
    setIsNumberValid,
    isNumberValid,
    checkUserExist,
    sendOTPMobile,
    messageID,
    setOTP,
    verifyOTP,
    verifyPhoto,
    uploadPhoto,
    registerCitizenDetails,
    conditionalRegisterValidator,
    generateAndSetIDCard,
    generateIDCard,
    ATCOptions,
    IDCard,
    setIDCard,
    downloadCitizenID,
    isLoading,
    agreement,
    setAgreement,
    modelOptions,
    religionOptions,
    occupationOptions,
    handleSimilarCitizenSelect,
    handleNewRegistration,
  };

  return <NewCitizenContext.Provider value={payload}>{children}</NewCitizenContext.Provider>;
};

export default NewCitizenProvider;
